import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

interface Branch {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
}

interface Admin {
  user_id: string;
  full_name: string;
  email: string;
  branch_id: string | null;
}

const BranchManagement = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [branches, setBranches] = useState<Branch[]>([]);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [deletingBranchId, setDeletingBranchId] = useState<string | null>(null);
  const [selectedBranchForAssign, setSelectedBranchForAssign] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  useEffect(() => {
    checkAuth();
    loadData();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("full_name")
      .eq("id", session.user.id)
      .single();

    if (profile) {
      setUserName(profile.full_name);
    }

    // Check if user is super admin
    const { data: isSuperAdmin } = await supabase.rpc('has_role', {
      _user_id: session.user.id,
      _role: 'super_admin'
    });

    if (!isSuperAdmin) {
      toast.error("Access denied. Super admin only.");
      navigate("/dashboard");
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load branches
      const { data: branchesData, error: branchesError } = await supabase
        .from("batches")
        .select("*")
        .order("created_at", { ascending: false });

      if (branchesError) throw branchesError;
      setBranches(branchesData || []);

      // Load admins with their branch assignments
      const { data: adminRoles, error: adminError } = await supabase
        .from("user_roles")
        .select("user_id, branch_id")
        .eq("role", "admin");

      if (adminError) throw adminError;

      // Get profile info for admins
      const userIds = adminRoles.map(r => r.user_id);
      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds);

      if (profileError) throw profileError;

      // Combine admin data
      const adminsData = adminRoles.map(role => {
        const profile = profiles.find(p => p.id === role.user_id);
        return {
          user_id: role.user_id,
          full_name: profile?.full_name || "Unknown",
          email: profile?.email || "Unknown",
          branch_id: role.branch_id,
        };
      });

      setAdmins(adminsData);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (branch?: Branch) => {
    if (branch) {
      setEditingBranch(branch);
      setFormData({
        name: branch.name,
        description: branch.description || "",
      });
    } else {
      setEditingBranch(null);
      setFormData({ name: "", description: "" });
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingBranch(null);
    setFormData({ name: "", description: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingBranch) {
        const { error } = await supabase
          .from("batches")
          .update({
            name: formData.name,
            description: formData.description,
          })
          .eq("id", editingBranch.id);

        if (error) throw error;
        toast.success("Branch updated successfully");
      } else {
        const { error } = await supabase
          .from("batches")
          .insert({
            name: formData.name,
            description: formData.description,
          });

        if (error) throw error;
        toast.success("Branch created successfully");
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error("Error saving branch:", error);
      toast.error("Failed to save branch");
    }
  };

  const handleDeleteClick = (branchId: string) => {
    setDeletingBranchId(branchId);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingBranchId) return;

    try {
      const { error } = await supabase
        .from("batches")
        .delete()
        .eq("id", deletingBranchId);

      if (error) throw error;
      toast.success("Branch deleted successfully");
      setShowDeleteDialog(false);
      setDeletingBranchId(null);
      loadData();
    } catch (error) {
      console.error("Error deleting branch:", error);
      toast.error("Failed to delete branch");
    }
  };

  const handleAssignBranch = async (adminUserId: string, branchId: string | null) => {
    try {
      const { error } = await supabase
        .from("user_roles")
        .update({ branch_id: branchId })
        .eq("user_id", adminUserId)
        .eq("role", "admin");

      if (error) throw error;
      toast.success("Admin branch assignment updated");
      loadData();
    } catch (error) {
      console.error("Error assigning branch:", error);
      toast.error("Failed to assign branch");
    }
  };

  const getBranchName = (branchId: string | null) => {
    if (!branchId) return "No Branch";
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || "Unknown";
  };

  if (loading) {
    return (
      <Layout userName={userName}>
        <div className="space-y-6">
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout userName={userName}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold">Branch Management</h1>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Branch
          </Button>
        </div>

        {/* Branches List */}
        <Card>
          <CardHeader>
            <CardTitle>Branches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {branches.length === 0 ? (
                <p className="text-muted-foreground">No branches found</p>
              ) : (
                branches.map((branch) => (
                  <div
                    key={branch.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold">{branch.name}</h3>
                      {branch.description && (
                        <p className="text-sm text-muted-foreground">{branch.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Admins assigned: {admins.filter(a => a.branch_id === branch.id).length}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDialog(branch)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(branch.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Admin Assignments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Admin Branch Assignments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {admins.length === 0 ? (
                <p className="text-muted-foreground">No admins found</p>
              ) : (
                admins.map((admin) => (
                  <div
                    key={admin.user_id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold">{admin.full_name}</h3>
                      <p className="text-sm text-muted-foreground">{admin.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary">{getBranchName(admin.branch_id)}</Badge>
                      <Select
                        value={admin.branch_id || "none"}
                        onValueChange={(value) =>
                          handleAssignBranch(admin.user_id, value === "none" ? null : value)
                        }
                      >
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Select branch" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Branch</SelectItem>
                          {branches.map((branch) => (
                            <SelectItem key={branch.id} value={branch.id}>
                              {branch.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingBranch ? "Edit Branch" : "Add New Branch"}
              </DialogTitle>
              <DialogDescription>
                {editingBranch
                  ? "Update the branch details below"
                  : "Create a new branch for organizing admins and students"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Branch Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingBranch ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Branch</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this branch? This action cannot be undone.
                All admins assigned to this branch will be unassigned.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletingBranchId(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BranchManagement;
